/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import * as Sentry from '@sentry/react';

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen w-full bg-[#0f1419] flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <p className="text-white/30 text-[10px] font-bold uppercase tracking-[0.2em] mb-3">
              Something went wrong
            </p>
            <h1 className="text-white font-black text-3xl tracking-tight mb-4">
              Unexpected Error
            </h1>
            <p className="text-white/40 text-sm mb-8 leading-relaxed">
              {this.state.error?.message ?? 'An unknown error occurred.'}
            </p>
            <button
              onClick={this.handleReload}
              className="bg-white text-[#0f1419] py-3 px-8 rounded-full font-black uppercase text-[10px] tracking-[0.2em] hover:scale-105 active:scale-95 transition-all"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
